import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useSession } from "@clerk/clerk-react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { UniversalTable } from "shared/components/UniversalTable";
import { TablePagination } from "components/TablePagination";
import { ContactImportModal } from "./components/contactImportModal";
import { CONTACTS_TABLE_CONFIG, COMPANIES_TABLE_CONFIG } from "./contactsTableConfig";
import { Phone, Download, Upload, Repeat, Trash, PencilSimple, Plus } from "@phosphor-icons/react";
import ButtonNew from "components/buttonNew/buttonNew";
import { toast } from "react-toastify";
import { ContactPickerButton } from "./components/ContactPickerButton";
import { ContactTags } from "shared/types/contacts";
import UniversalPickerModal from "shared/components/UniversalPickerModal/UniversalPickerModal";

// Memoize ContactTags options at module level
const CONTACT_TAG_OPTIONS = Object.values(ContactTags);

// A building/unit a contact or company is linked to (gebouwcontact or tenant).
interface BuildingLink {
  buildingId: string;
  buildingLabel: string;
  unitId: string | null;
  unitLabel: string | null;
}

// Renders the "Gebouw / Eenheid" cell. A contact/company can be linked to
// multiple buildings and units (especially gebouwcontacten), so we list them all.
const renderBuildingLinks = (links?: BuildingLink[]) => {
  if (!links || links.length === 0) {
    return <span className="text-neutral-secondary-mid">-</span>;
  }

  return (
    <div className="flex flex-col gap-0.5">
      {links.map((link, index) => (
        <span
          key={`${link.buildingId}-${link.unitId ?? ''}-${index}`}
          className="text-sm text-neutral-primary leading-tight"
        >
          <span className="font-medium">{link.buildingLabel}</span>
          {link.unitLabel && (
            <span className="text-neutral-secondary-mid"> · {link.unitLabel}</span>
          )}
        </span>
      ))}
    </div>
  );
};

const ContactsComponent = () => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Stable session ID to prevent re-fetching when session object reference changes
  const sessionIdRef = useRef<string | null>(null);
  const currentSessionId = session?.id || null;
  
  // Only update session ID ref if it actually changed
  if (currentSessionId !== sessionIdRef.current) {
    sessionIdRef.current = currentSessionId;
  }

  // Track renders
  const renderCountRef = useRef(0);
  renderCountRef.current += 1;

  // Tab state
  const [activeTab, setActiveTabInternal] = useState<'contacts' | 'companies'>('contacts');
  
  const setActiveTab = setActiveTabInternal;

  // Data states
  const [contacts, setContacts] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [isLoadingContacts, setIsLoadingContacts] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  // Page-based pagination states (< > arrows, like helpdesk)
  const [currentContactsPage, setCurrentContactsPage] = useState(1);
  const [currentCompaniesPage, setCurrentCompaniesPage] = useState(1);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [companiesTotal, setCompaniesTotal] = useState(0);
  const pageSize = 50;

  // Initialize filter states from URL (so first fetch has correct filters)
  const initialSearchText = searchParams.get('search') || '';
  const initialTagFilters = searchParams.get('tags')?.split(',').filter(Boolean) || [];
  const initialBuildingFilters = searchParams.get('building')?.split(',').filter(Boolean) || [];

  const [searchText, setSearchText] = useState(initialSearchText);
  const [tagFilters, setTagFilters] = useState<string[]>(initialTagFilters);
  const [buildingFilters, setBuildingFilters] = useState<string[]>(initialBuildingFilters);


  // URL restoration state (prevent FilterBar from overriding during restoration)
  const [isRestoringFromURL, setIsRestoringFromURL] = useState(false);

  // Refresh trigger for manual data reload
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Selection states
  const [selectedContacts, setSelectedContacts] = useState<Set<number>>(new Set());
  const [selectedCompanies, setSelectedCompanies] = useState<Set<number>>(new Set());

  // Import modal state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importContacts, setImportContacts] = useState<any[]>([]);
  const [isImporting, setIsImporting] = useState(false);

  // Delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState<{ ids: number[]; type: 'contact' | 'company' }>({ ids: [], type: 'contact' });

  // Centralized picker state (like helpdesk)
  const [pickerState, setPickerState] = useState<{
    isOpen: boolean;
    entityId: number | null;
    entityType: 'contact' | 'company' | null;
    currentTags: string[];
  }>({ isOpen: false, entityId: null, entityType: null, currentTags: [] });

  // Import modal event listener
  useEffect(() => {
    const handleOpenImportModal = (event: CustomEvent) => {
      setImportContacts(event.detail.contacts);
      setIsImportModalOpen(true);
    };

    window.addEventListener('openContactImportModal', handleOpenImportModal as EventListener);

    return () => {
      window.removeEventListener('openContactImportModal', handleOpenImportModal as EventListener);
    };
  }, []);

  // Centralized tags picker event listener (like helpdesk)
  useEffect(() => {
    const handleOpenTagsPicker = (event: CustomEvent) => {
      const { entityId, entityType, currentTags } = event.detail;
      setPickerState({
        isOpen: true,
        entityId,
        entityType,
        currentTags: [...currentTags] // Snapshot
      });
    };

    window.addEventListener('contact-tags-picker', handleOpenTagsPicker as EventListener);

    return () => {
      window.removeEventListener('contact-tags-picker', handleOpenTagsPicker as EventListener);
    };
  }, []);

  // Button handlers - memoized to prevent re-renders
  const handleRefresh = useCallback(async () => {
    await fetchWithAuth(
      `${API_URL}/odoo-sync/contacts`,
      { method: "GET" },
      session,
    );
    toast.success("Contacten gesynchroniseerd met Odoo");
    // Trigger re-fetch by incrementing refresh trigger
    setRefreshTrigger(prev => prev + 1);
  }, [session]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/excel/export-contact-template`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contact-import-template-${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Template gedownload!");
      } else {
        toast.error("Fout bij het downloaden van template");
      }
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Fout bij het downloaden van template");
    }
  }, [session]);

  const handleFileUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setIsImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetchWithAuth(
        `${API_URL}/excel/import-contacts`,
        {
          method: "POST",
          body: formData,
        },
        session,
      );

      if (response.ok) {
        const data = await response.json();

        if (data.contacts && data.contacts.length > 0) {
          // Open import modal with the data
          window.dispatchEvent(new CustomEvent('openContactImportModal', {
            detail: { contacts: data.contacts }
          }));
          toast.success(`${data.contacts.length} contacten gelezen uit Excel bestand`);
        } else {
          toast.warning("Geen contacten gevonden in het Excel bestand");
        }
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Fout bij het importeren van contacten");
      }
    } catch (error: any) {
      toast.error("Fout bij het importeren van contacten: " + error.message);
    } finally {
      setIsImporting(false);
      // Reset file input
      event.target.value = '';
    }
  }, [session]);

  const handleButtonClick = useCallback(() => {
    const fileInput = document.querySelector('input[type="file"]#contact-file-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }, []);

  // Fetch a single page of contacts. Backend returns { data, totalCount }.
  const fetchContacts = useCallback(async (page: number) => {
    if (!session) return;

    setIsLoadingContacts(true);

    const params: Record<string, string> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortField: 'id',
      sortDirection: 'DESC'
    };

    if (searchText && searchText.trim()) {
      params.searchText = searchText.trim();
    }
    if (tagFilters.length > 0) {
      params.tags = tagFilters.join(',');
    }
    if (buildingFilters.length > 0) {
      params.building = buildingFilters.join(',');
    }

    const queryParams = new URLSearchParams(params).toString();

    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact?${queryParams}`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      setContacts(result.data ?? []);
      setContactsTotal(result.totalCount ?? 0);
    } catch (error) {
      console.error("❌ Error fetching contacts:", error);
      toast.error("Fout bij ophalen van contacten");
    } finally {
      setIsLoadingContacts(false);
    }
  }, [session, pageSize, searchText, tagFilters, buildingFilters]);

  // Fetch a single page of companies. Backend returns { data, totalCount }.
  const fetchCompanies = useCallback(async (page: number) => {
    if (!session) return;

    setIsLoadingCompanies(true);

    const params: Record<string, string> = {
      page: page.toString(),
      pageSize: pageSize.toString(),
      sortField: 'id',
      sortDirection: 'DESC'
    };

    if (searchText && searchText.trim()) {
      params.searchText = searchText.trim();
    }
    if (tagFilters.length > 0) {
      params.tags = tagFilters.join(',');
    }
    if (buildingFilters.length > 0) {
      params.building = buildingFilters.join(',');
    }

    const queryParams = new URLSearchParams(params).toString();

    try {
      const response = await fetchWithAuth(
        `${API_URL}/contact/companies?${queryParams}`,
        { method: "GET" },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      setCompanies(result.data ?? []);
      setCompaniesTotal(result.totalCount ?? 0);
    } catch (error) {
      console.error("❌ Error fetching companies:", error);
      toast.error("Fout bij ophalen van bedrijven");
    } finally {
      setIsLoadingCompanies(false);
    }
  }, [session, pageSize, searchText, tagFilters, buildingFilters]);

  // Reset to page 1 whenever the search term or filters change
  const filterKey = useMemo(
    () => JSON.stringify({ searchText, tagFilters, buildingFilters }),
    [searchText, tagFilters, buildingFilters]
  );

  useEffect(() => {
    setCurrentContactsPage(1);
    setCurrentCompaniesPage(1);
  }, [filterKey]);

  // Fetch the active tab's current page whenever page, filters or tab change
  useEffect(() => {
    if (!session) return;
    if (activeTab === 'contacts') {
      fetchContacts(currentContactsPage);
    } else {
      fetchCompanies(currentCompaniesPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, activeTab, currentContactsPage, currentCompaniesPage, fetchContacts, fetchCompanies, refreshTrigger]);

  // Filter sections - tags + building (tab switching is handled by the tab buttons)
  const filterSections = useMemo(() => [
    {
      key: 'building',
      label: 'Gebouw',
      options: [] as { key: string; label: string }[],
      color: 'moss' as const,
      // Fetched from backend: buildings that have linked contacts/companies or active tenants
      fetchFromAPI: {
        endpoint: '/contact/filter-options/buildings',
      },
    },
    {
      key: 'tags',
      label: 'Tags',
      options: Object.values(ContactTags).map(tag => ({ key: tag, label: tag })),
      color: 'neutral' as const,
    }
  ], []);

  // No longer need client-side filtering - backend handles it
  // Just use the contacts and companies directly from state

  // Filter update handler
  const handleFilterChange = useCallback((key: string, values: string[]) => {
    if (key === 'type') {
      const newTab = values.length > 0 ? values[0] as 'contacts' | 'companies' : 'contacts';
      setActiveTab(newTab);
    } else if (key === 'tags') {
      setTagFilters(values);
    } else if (key === 'building') {
      setBuildingFilters(values);
    }
  }, []);

  // URL restoration handler
  const handleFiltersFromURL = useCallback((urlFilters: any) => {
    setIsRestoringFromURL(true);

    if (urlFilters.search !== undefined && urlFilters.search !== searchText) {
      setSearchText(urlFilters.search);
    }

    const sections = urlFilters.filterSections || {};

    const newTags = sections.tags || [];
    const tagsChanged = JSON.stringify(newTags.sort()) !== JSON.stringify([...tagFilters].sort());
    if (tagsChanged) {
      setTagFilters(newTags);
    }

    const newBuildings = sections.building || [];
    const buildingsChanged = JSON.stringify(newBuildings.sort()) !== JSON.stringify([...buildingFilters].sort());
    if (buildingsChanged) {
      setBuildingFilters(newBuildings);
    }

    setTimeout(() => {
      setIsRestoringFromURL(false);
    }, 100);
  }, [searchText, tagFilters, buildingFilters]);

  // Saved searches state and handlers (for feature parity with helpdesk)
  const [savedSearches] = useState<any[]>([]);
  
  const saveCurrentSearch = useCallback(() => {
    // TODO: Implement saved searches persistence if needed
    console.log('Save current search:', { searchText, tagFilters });
  }, [searchText, tagFilters]);

  const applySavedSearch = useCallback((search: any) => {
    // TODO: Implement saved search restoration if needed
    console.log('Apply saved search:', search);
  }, []);

  // Monitor URL changes and sync with state
  // This ensures that when navigating back from detail page, filters are applied
  const urlMonitorMountRef = useRef(true);
  useEffect(() => {
    // Skip on first mount since state is already initialized from URL
    if (urlMonitorMountRef.current) {
      urlMonitorMountRef.current = false;
      return;
    }
    
    const urlSearch = searchParams.get('search') || '';
    const urlTags = searchParams.get('tags')?.split(',').filter(Boolean) || [];
    const urlBuildings = searchParams.get('building')?.split(',').filter(Boolean) || [];

    // Check if URL params differ from current state
    const searchDiffers = urlSearch !== searchText;
    const tagsDiffer = JSON.stringify(urlTags.sort()) !== JSON.stringify([...tagFilters].sort());
    const buildingsDiffer = JSON.stringify(urlBuildings.sort()) !== JSON.stringify([...buildingFilters].sort());

    if (searchDiffers || tagsDiffer || buildingsDiffer) {
      // Update state to match URL
      if (searchDiffers) {
        setSearchText(urlSearch);
      }
      if (tagsDiffer) {
        setTagFilters(urlTags);
      }
      if (buildingsDiffer) {
        setBuildingFilters(urlBuildings);
      }
    }
  }, [searchParams]); // Only depend on searchParams, not the state values to avoid loops

  // Delete handlers
  const handleDeleteContact = useCallback((contactId: number) => {
    setItemsToDelete({ ids: [contactId], type: 'contact' });
    setDeleteModalOpen(true);
  }, []);

  const handleDeleteCompany = useCallback((companyId: number) => {
    setItemsToDelete({ ids: [companyId], type: 'company' });
    setDeleteModalOpen(true);
  }, []);

  const handleBulkDelete = useCallback((type: 'contact' | 'company') => {
    const ids = type === 'contact'
      ? Array.from(selectedContacts)
      : Array.from(selectedCompanies);

    if (ids.length === 0) {
      toast.warning('Selecteer eerst items om te verwijderen');
      return;
    }

    setItemsToDelete({ ids, type });
    setDeleteModalOpen(true);
  }, [selectedContacts, selectedCompanies]);

  const confirmDelete = useCallback(async () => {
    const { ids, type } = itemsToDelete;

    try {
      // Delete each item
      const deletePromises = ids.map(async (id) => {
        const endpoint = type === 'contact'
          ? `${API_URL}/contact/delete-contact/${id}`
          : `${API_URL}/contact/delete-company/${id}`;

        return fetchWithAuth(endpoint, { method: 'DELETE' }, session);
      });

      const results = await Promise.allSettled(deletePromises);

      const failures: string[] = [];
      let successCount = 0;

      for (const result of results) {
        if (result.status === 'fulfilled') {
          const response = result.value;
          if (response.ok) {
            successCount++;
          } else {
            try {
              const errorBody = await response.json();
              failures.push(errorBody.message || 'Onbekende fout');
            } catch {
              failures.push(`HTTP ${response.status}`);
            }
          }
        } else {
          failures.push(result.reason?.message || 'Netwerkfout');
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} ${type === 'contact' ? 'contact(en)' : 'bedrijf/bedrijven'} verwijderd`);
      }
      if (failures.length > 0) {
        toast.error(failures[0]);
      }

      // Refresh data
      if (type === 'contact') {
        fetchContacts(currentContactsPage);
        setSelectedContacts(new Set());
      } else {
        fetchCompanies(currentCompaniesPage);
        setSelectedCompanies(new Set());
      }

      setDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Fout bij verwijderen');
    }
  }, [itemsToDelete, session, fetchContacts, fetchCompanies, currentContactsPage, currentCompaniesPage]);

  // Handle tags update from centralized picker
  const handleTagsUpdate = useCallback(async (selectedTags: string[]) => {
    if (!pickerState.entityId || !pickerState.entityType) return;

    try {
      const endpoint = pickerState.entityType === 'contact'
        ? `${API_URL}/contact/update-contact/${pickerState.entityId}`
        : `${API_URL}/contact/update-company/${pickerState.entityId}`;

      const response = await fetchWithAuth(
        endpoint,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tags: selectedTags })
        },
        session
      );

      if (response.ok) {
        toast.success(`Tags bijgewerkt voor ${pickerState.entityType === 'contact' ? 'contact' : 'bedrijf'}`);

        // Refresh appropriate data
        if (pickerState.entityType === 'contact') {
          fetchContacts(currentContactsPage);
        } else {
          fetchCompanies(currentCompaniesPage);
        }

        // Close picker
        setPickerState({ isOpen: false, entityId: null, entityType: null, currentTags: [] });
      } else {
        throw new Error('Failed to update tags');
      }
    } catch (error) {
      console.error('Error updating tags:', error);
      toast.error('Fout bij bijwerken van tags');
    }
  }, [pickerState, session, fetchContacts, fetchCompanies, currentContactsPage, currentCompaniesPage]);

  // Bulk actions
  const bulkActions = useMemo(() => {
    const selectedIds = activeTab === 'contacts' ? selectedContacts : selectedCompanies;
    const selectedCount = selectedIds.size;

    if (selectedCount === 0) return [];

    return [
      {
        label: `Verwijder (${selectedCount})`,
        onClick: () => handleBulkDelete(activeTab === 'contacts' ? 'contact' : 'company'),
        variant: 'red' as const,
        icon: (
          <Trash className="w-4 h-4" />
        )
      }
    ];
  }, [activeTab, selectedContacts, selectedCompanies, handleBulkDelete]);

  // Memoize render callbacks to prevent UniversalTable re-renders
  const renderContactRow = useCallback((contact: any, context: any) => {
    // Helper to navigate with preserved URL params
    const navigateToContact = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const currentParams = window.location.search;
      window.location.href = `/contact/${contact.id}${currentParams}`;
    };

    return [
      // Checkbox
      {
        id: 'checkbox',
        content: (
          <input
            type="checkbox"
            className="w-4 h-4 rounded-sm border border-neutral-primary/30 cursor-pointer"
            style={{ accentColor: '#5B7304' }}
            checked={context.isSelected}
            onChange={(e) => {
              e.stopPropagation();
              context.onToggleSelect(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
      // ID
      {
        id: 'id',
        content: (
          <span className="text-neutral-secondary-mid text-sm">
            {contact.id}
          </span>
        )
      },
      // First Name
      {
        id: 'firstName',
        content: (
          <a
            href={`/contact/${contact.id}${window.location.search}`}
            onClick={navigateToContact}
            className="text-neutral-primary hover:text-moss-primary font-medium cursor-pointer"
          >
            {contact.firstName}
          </a>
        )
      },
      // Last Name
      {
        id: 'lastName',
        content: (
          <a
            href={`/contact/${contact.id}${window.location.search}`}
            onClick={navigateToContact}
            className="text-neutral-primary hover:text-moss-primary font-medium cursor-pointer"
          >
            {contact.lastName}
          </a>
        )
      },
      // Email
      {
        id: 'email',
        content: contact.email ? (
          <a
            href={`mailto:${contact.email}`}
            className="text-moss-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {contact.email}
          </a>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Company
      {
        id: 'company',
        content: contact.company ? (
          <a
            href={`/company/${contact.company.id}${window.location.search}`}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              const currentParams = window.location.search;
              window.location.href = `/company/${contact.company.id}${currentParams}`;
            }}
            className="text-neutral-primary hover:text-moss-primary cursor-pointer"
          >
            {contact.company.name}
          </a>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Job Title
      {
        id: 'jobTitle',
        content: contact.jobTitle || <span className="text-neutral-secondary-mid">-</span>
      },
      // Mobile Phone
      {
        id: 'phone_mobile',
        content: contact.phone_mobile ? (
          <a
            href={`tel:${contact.phone_mobile}`}
            className="flex items-center gap-1 text-moss-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Phone className="w-4 h-4" />
            {contact.phone_mobile}
          </a>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Direct Phone
      {
        id: 'phone_direct',
        content: contact.phone_direct ? (
          <a
            href={`tel:${contact.phone_direct}`}
            className="flex items-center gap-1 text-moss-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Phone className="w-4 h-4" />
            {contact.phone_direct}
          </a>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Building / Unit links (gebouwcontacten + huurders via huurcontract)
      {
        id: 'buildingLinks',
        content: renderBuildingLinks(contact.buildingLinks)
      },
      // Tags
      {
        id: 'tags',
        content: (
          <ContactPickerButton
            entityId={contact.id}
            entityType="contact"
            currentTags={contact.tags || []}
          />
        )
      },
      // Actions
      {
        id: 'actions',
        content: (
          <div className="flex items-center gap-1 justify-center whitespace-nowrap shrink-0">
            <a
              href={`/contact/${contact.id}${window.location.search}`}
              onClick={navigateToContact}
              className="text-moss-primary hover:text-moss-primary/80 transition-colors cursor-pointer"
              title="Bewerk contact"
            >
              <PencilSimple className="w-4 h-4" />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteContact(contact.id);
              }}
              className="text-red-600 hover:text-red-700 transition-colors"
              title="Verwijder contact"
            >
              <Trash className="w-4 h-4" />
            </button>
          </div>
        )
      }
    ];
  }, [handleDeleteContact]);

  // Render company row
  const renderCompanyRow = useCallback((company: any, context: any) => {
    // Helper to navigate with preserved URL params
    const navigateToCompany = (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const currentParams = window.location.search;
      window.location.href = `/company/${company.id}${currentParams}`;
    };

    return [
      // Checkbox
      {
        id: 'checkbox',
        content: (
          <input
            type="checkbox"
            className="w-4 h-4 rounded-sm border border-neutral-primary/30 cursor-pointer"
            style={{ accentColor: '#5B7304' }}
            checked={context.isSelected}
            onChange={(e) => {
              e.stopPropagation();
              context.onToggleSelect(e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        )
      },
      // ID
      {
        id: 'id',
        content: (
          <span className="text-neutral-secondary-mid text-sm">
            {company.id}
          </span>
        )
      },
      // Name
      {
        id: 'name',
        content: (
          <a
            href={`/company/${company.id}${window.location.search}`}
            onClick={navigateToCompany}
            className="text-neutral-primary hover:text-moss-primary font-medium cursor-pointer"
          >
            {company.name}
          </a>
        )
      },
      // Email
      {
        id: 'email',
        content: company.email ? (
          <a
            href={`mailto:${company.email}`}
            className="text-moss-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {company.email}
          </a>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Phone
      {
        id: 'phone_direct',
        content: company.phone_direct ? (
          <a
            href={`tel:${company.phone_direct}`}
            className="flex items-center gap-1 text-moss-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            <Phone className="w-4 h-4" />
            {company.phone_direct}
          </a>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // Address
      {
        id: 'address',
        content: company.address ? (
          <span className="text-neutral-primary">
            {company.address.street || ''} {company.address.city || ''}
          </span>
        ) : (
          <span className="text-neutral-secondary-mid">-</span>
        )
      },
      // VAT Number
      {
        id: 'vatNumber',
        content: company.vatNumber || <span className="text-neutral-secondary-mid">-</span>
      },
      // Building / Unit links (gebouwcontacten + huurders via huurcontract)
      {
        id: 'buildingLinks',
        content: renderBuildingLinks(company.buildingLinks)
      },
      // Tags
      {
        id: 'tags',
        content: (
          <ContactPickerButton
            entityId={company.id}
            entityType="company"
            currentTags={company.tags || []}
          />
        )
      },
      // Actions
      {
        id: 'actions',
        content: (
          <div className="flex items-center gap-1 justify-center whitespace-nowrap shrink-0">
            <a
              href={`/company/${company.id}${window.location.search}`}
              onClick={navigateToCompany}
              className="text-moss-primary hover:text-moss-primary/80 transition-colors cursor-pointer"
              title="Bewerk bedrijf"
            >
              <PencilSimple className="w-4 h-4" />
            </a>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteCompany(company.id);
              }}
              className="text-red-600 hover:text-red-700 transition-colors"
              title="Verwijder bedrijf"
            >
              <Trash className="w-4 h-4" />
            </button>
          </div>
        )
      }
    ];
  }, [handleDeleteCompany]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>Contacten &mdash; Yally</title>
        </Helmet>
      </HelmetProvider>

      <div className="flex flex-col gap-0">
        {/* Sticky Header with Tabs and Buttons */}
        <div className="sticky top-0 z-20">
          <div className="flex items-center justify-between px-4 py-3">
            {/* Left side: Tabs */}
            <div className="flex border-b border-neutral-primary/15">
              <button
                onClick={() => setActiveTab('contacts')}
                className={`px-6 py-3 font-medium transition-colors relative ${
                  activeTab === 'contacts'
                    ? 'text-moss-primary'
                    : 'text-neutral-secondary-mid hover:text-neutral-primary'
                }`}
              >
                <span className="flex items-center gap-2">
                  Contacten
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === 'contacts'
                      ? 'bg-moss-primary text-white'
                      : 'bg-neutral-primary/10 text-neutral-primary'
                  }`}>
                    {contactsTotal}
                  </span>
                </span>
                {activeTab === 'contacts' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-moss-primary" />
                )}
              </button>
              <button
                onClick={() => setActiveTab('companies')}
                className={`px-6 py-3 font-medium transition-colors relative ${
                  activeTab === 'companies'
                    ? 'text-moss-primary'
                    : 'text-neutral-secondary-mid hover:text-neutral-primary'
                }`}
              >
                <span className="flex items-center gap-2">
                  Bedrijven
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    activeTab === 'companies'
                      ? 'bg-moss-primary text-white'
                      : 'bg-neutral-primary/10 text-neutral-primary'
                  }`}>
                    {companiesTotal}
                  </span>
                </span>
                {activeTab === 'companies' && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-moss-primary" />
                )}
              </button>
            </div>

            {/* Right side: Pagination + Action Buttons */}
            <div className="flex items-center gap-2">
              <TablePagination
                page={activeTab === 'contacts' ? currentContactsPage : currentCompaniesPage}
                pageSize={pageSize}
                totalCount={activeTab === 'contacts' ? contactsTotal : companiesTotal}
                onPageChange={activeTab === 'contacts' ? setCurrentContactsPage : setCurrentCompaniesPage}
                isLoading={activeTab === 'contacts' ? isLoadingContacts : isLoadingCompanies}
              />
              {activeTab === 'contacts' && (
                <ButtonNew
                  variant="primaryMoss"
                  onClick={() => navigate('/contact/new')}
                  icon={<Plus />}
                  label="Nieuw contact"
                />
              )}
              {activeTab === 'companies' && (
                <ButtonNew
                  variant="primaryMoss"
                  onClick={() => navigate('/company/new')}
                  icon={<Plus />}
                  label="Nieuw bedrijf"
                />
              )}
              <ButtonNew
                variant="primaryMoss"
                onClick={handleDownloadTemplate}
                icon={<Download />}
                label="Download Template"
              />
              <ButtonNew
                variant="primaryBrick"
                onClick={handleButtonClick}
                icon={<Upload />}
                label={isImporting ? "Importeren..." : "Importeer Excel"}
                isDisabled={isImporting}
              />
              <input
                id="contact-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
                disabled={isImporting}
              />
              <ButtonNew
                variant="primaryMoss"
                onClick={handleRefresh}
                icon={<Repeat />}
                label="Sync met Odoo"
              />
            </div>
          </div>
        </div>

        {/* Contacts Table */}
        {activeTab === 'contacts' && (
          <UniversalTable
            {...CONTACTS_TABLE_CONFIG}
            tableId="contacts"
            rows={contacts.map(c => ({ ...c, type: 'contacts' }))}
            title="Contacten"
            description="Beheer al uw contactpersonen"
            renderRow={renderContactRow}
            selectedRows={selectedContacts}
            onSelectionChange={setSelectedContacts}
            onUpdate={async () => {
              // Refresh after update
              await fetchContacts(currentContactsPage);
            }}
            excelExportConfig={{
              filename: 'contacts',
              sheetName: 'Contacten',
              buttonText: 'Naar Excel exporteren',
              excludeColumns: ['checkbox', 'actions', 'buildingLinks']
            }}
            searchConfig={{
              searchPlaceholder: "Zoeken op naam, email...",
              filterSections,
              searchValue: searchText,
              onSearchChange: (value) => {
                if (!isRestoringFromURL) {
                  setSearchText(value);
                }
              },
              activeFilters: {
                tags: tagFilters,
                building: buildingFilters
              },
              onFilterChange: handleFilterChange,
              // Search/filtering/sorting all happen on the backend; never
              // re-filter the already-paginated rows client-side.
              serverSideFiltering: true,
              bulkActions,
              syncWithURL: true,
              urlParamPrefix: "",
              onFiltersFromURL: handleFiltersFromURL,
              // Saved searches
              savedSearches,
              onSaveCurrentSearch: saveCurrentSearch,
              onApplySavedSearch: applySavedSearch,
            }}
          />
        )}

        {/* Companies Table */}
        {activeTab === 'companies' && (
          <UniversalTable
            {...COMPANIES_TABLE_CONFIG}
            tableId="companies"
            rows={companies.map(c => ({ ...c, type: 'companies' }))}
            title="Bedrijven"
            description="Beheer al uw bedrijven"
            renderRow={renderCompanyRow}
            selectedRows={selectedCompanies}
            onSelectionChange={setSelectedCompanies}
            onUpdate={async () => {
              // Refresh after update
              await fetchCompanies(currentCompaniesPage);
            }}
            excelExportConfig={{
              filename: 'companies',
              sheetName: 'Bedrijven',
              buttonText: 'Naar Excel exporteren',
              excludeColumns: ['checkbox', 'actions', 'buildingLinks']
            }}
            searchConfig={{
              searchPlaceholder: "Zoeken op naam, email...",
              filterSections,
              searchValue: searchText,
              onSearchChange: (value) => {
                if (!isRestoringFromURL) {
                  setSearchText(value);
                }
              },
              activeFilters: {
                tags: tagFilters,
                building: buildingFilters
              },
              onFilterChange: handleFilterChange,
              // Search/filtering/sorting all happen on the backend; never
              // re-filter the already-paginated rows client-side.
              serverSideFiltering: true,
              bulkActions,
              syncWithURL: true,
              urlParamPrefix: "",
              onFiltersFromURL: handleFiltersFromURL,
              // Saved searches
              savedSearches,
              onSaveCurrentSearch: saveCurrentSearch,
              onApplySavedSearch: applySavedSearch,
            }}
          />
        )}

      </div>

      <ContactImportModal
        isOpen={isImportModalOpen}
        onOpenChange={() => setIsImportModalOpen(false)}
        contacts={importContacts}
      />

      {/* Delete Confirmation Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-neutral-primary mb-2">
                Verwijder {itemsToDelete.type === 'contact' ? 'contact(en)' : 'bedrijf/bedrijven'}
              </h3>
              <p className="text-neutral-secondary-mid mb-6">
                Weet u zeker dat u {itemsToDelete.ids.length} {itemsToDelete.type === 'contact' ? 'contact(en)' : 'bedrijf/bedrijven'} wilt verwijderen?
                Deze actie kan niet ongedaan worden gemaakt.
              </p>
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => setDeleteModalOpen(false)}
                  className="px-4 py-2 rounded-md border border-neutral-primary/20 text-neutral-primary hover:bg-neutral-primary/5 transition-colors"
                >
                  Annuleren
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  Verwijderen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Centralized Tags Picker Modal */}
      {pickerState.isOpen && (
        <UniversalPickerModal
          isOpen={pickerState.isOpen}
          mode="custom-tags"
          title={`Selecteer tags voor ${pickerState.entityType === 'contact' ? 'contact' : 'bedrijf'}`}
          onClose={() => setPickerState({ isOpen: false, entityId: null, entityType: null, currentTags: [] })}
          onConfirm={handleTagsUpdate}
          config={{
            customOptions: CONTACT_TAG_OPTIONS,
            multiSelect: true,
            defaultValue: pickerState.currentTags
          }}
        />
      )}
    </>
  );
};

// Export without memo to prevent mount/unmount issues
export default ContactsComponent;
